import React, { useState, useEffect } from 'react';
import { Anchor, Clock, MapPin, Navigation, Package, Settings, AlertCircle, CheckCircle, X, Ship } from 'lucide-react';
import { usePIM } from '../../context/PIMContext';

const DetailRow = ({ label, value, icon: Icon, extra }) => (
    <div className="mb-1.5">
        <div className="flex justify-between items-center mb-0.5">
            <span className="text-[10px] font-bold text-stone-500 uppercase flex items-center gap-1">
                {Icon && <Icon size={10} />} {label}
            </span>
            <Settings size={10} className="text-stone-600 cursor-pointer hover:text-stone-400" />
        </div>
        <div className="bg-stone-950/50 border border-stone-800 rounded px-2 py-1 flex justify-between items-center">
            <span className="text-stone-200 font-medium text-xs">{value}</span>
            {extra}
        </div>
    </div>
);

export const LiveTrackingDetails = () => {
    const { setPalletMiddleWidget, setPalletRightWidget, setIsPalletMode } = usePIM();

    // Mock Data - In a real app, this would come from props or context
    const shipment = {
        cargoId: "AWB-HKG-HAJ-2025",
        origin: {
            code: "HKG",
            name: "Hong Kong",
            date: "Nov 15, 2025"
        },
        destination: {
            code: "HAJ",
            name: "Hannover, Germany",
            date: "Dec 15, 2025"
        },
        status: "En-route (Halfway)",
        estimatedDuration: "30 Days",
        totalDistance: "18,500 km",
        remainingDistance: "9,200 km",
        eta: "Dec 15, 2025, 08:00",
        isOnSchedule: true,
        qty: "150 TEU (Containers)",
        progress: 50
    };

    const [countdown, setCountdown] = useState("");

    useEffect(() => {
        // Simple countdown simulation
        // Target: Dec 15
        const targetDate = new Date("2025-12-15T08:00:00").getTime();

        const updateCountdown = () => {
            const now = Date.now();
            const distance = targetDate - now;

            if (distance < 0) {
                setCountdown("Arrived");
                return;
            }

            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

            setCountdown(`${days}d ${hours}h ${minutes}m`);
        };

        updateCountdown(); // Initial
        const interval = setInterval(updateCountdown, 1000 * 60); // Update every minute is enough

        return () => clearInterval(interval);
    }, []);

    const handleClose = () => {
        setIsPalletMode(false);
        // Optional: Reset widgets to default if desired, but not strictly necessary as flip hides them
        setTimeout(() => {
            setPalletMiddleWidget('Pallet3DVisualizer');
            setPalletRightWidget('PalletControls');
        }, 500); // Wait for flip animation to finish
    };



    return (
        <div className="h-full flex flex-col bg-stone-900 rounded-lg border border-stone-700 overflow-hidden">
            {/* Header */}
            <div className="bg-stone-950 px-3 py-1.5 border-b border-stone-800 flex justify-between items-center shrink-0">
                <div>
                    <h3 className="text-stone-200 font-bold text-xs">Cargo ID: {shipment.cargoId}</h3>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-stone-500">Status:</span>
                        <span className="text-[10px] text-blue-400 font-medium px-1 py-0.5 bg-blue-900/20 rounded border border-blue-900/50">{shipment.status}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Ship size={16} className="text-blue-500" />
                    <button
                        onClick={handleClose}
                        className="text-stone-500 hover:text-stone-300 transition-colors p-1 rounded-md hover:bg-stone-800"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-stone-700 scrollbar-track-transparent">
                {/* Route */}
                <div className="flex items-center justify-between mb-3 bg-stone-800/30 p-2 rounded-lg border border-stone-800">
                    <div>
                        <div className="text-base font-bold text-stone-200">{shipment.origin.code}</div>
                        <div className="text-[9px] text-stone-500">{shipment.origin.date}</div>
                    </div>
                    <div className="flex-1 px-2 flex flex-col items-center">
                        <div className="text-[9px] text-stone-500 mb-0.5">{shipment.estimatedDuration}</div>
                        <div className="w-full h-0.5 bg-stone-700 relative">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-stone-900 p-0.5 rounded-full border border-stone-700">
                                <Ship size={10} className="text-blue-400" />
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-base font-bold text-stone-200">{shipment.destination.code}</div>
                        <div className="text-[9px] text-stone-500">{shipment.destination.date}</div>
                    </div>
                </div>

                <DetailRow label="Total Distance" value={shipment.totalDistance} icon={MapPin} />

                <DetailRow
                    label="Remaining Distance"
                    value={shipment.remainingDistance}
                    extra={
                        <div className={`flex items-center gap-1 text-[10px] font-medium ${shipment.isOnSchedule ? 'text-green-400' : 'text-red-400'}`}>
                            {shipment.isOnSchedule ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
                            {shipment.isOnSchedule ? 'On Schedule' : 'Delayed'}
                        </div>
                    }
                />

                <DetailRow label="ETA" value={shipment.eta} icon={Clock} />

                <DetailRow label="Qty" value={shipment.qty} icon={Package} />

                {/* Progress */}
                <div className="mt-2">
                    <div className="flex justify-between text-[10px] text-stone-400 mb-1">
                        <span>Progress: {shipment.progress}%</span>
                        <span className="font-mono">{countdown}</span>
                    </div>
                    <div className="h-1 bg-stone-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-green-500 transition-all duration-1000 ease-out"
                            style={{ width: `${shipment.progress}%` }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
